import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { enCentimes, enEuros, formaterDate, formaterMontant } from '../lib/format';
import type { AnneeScolaire } from '../types';

export function AnneesPage() {
  const { peut, entiteActive } = useAuth();
  const [modaleOuverte, setModaleOuverte] = useState(false);

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<AnneeScolaire[]>('/api/annees'),
    [entiteActive?.id],
  );

  // L'annee est COMMUNE a toutes les ecoles : la cloturer les fige TOUTES.
  // C'est la contrepartie du partage, et elle se dit avant de confirmer —
  // pas apres.
  const changerStatut = async (annee: AnneeScolaire, statut: AnneeScolaire['statut']) => {
    if (
      statut === 'cloturee' &&
      !window.confirm(
        `Cloturer ${annee.libelle} ?

` +
          `Cette annee est commune a TOUTES les ecoles : la cloturer fige ses montants ` +
          `et interdit toute saisie partout, pas seulement sur ${entiteActive?.code ?? 'cette ecole'}.

` +
          `Cette action ne s'annule pas ici.`,
      )
    ) {
      return;
    }
    await api.patch(`/api/annees/${annee.id}/statut`, { statut });
    recharger();
  };

  const colonnes: Colonne<AnneeScolaire>[] = [
    { cle: 'libelle', entete: 'Annee', rendu: (a) => <span className="font-medium">{a.libelle}</span> },
    {
      cle: 'periode',
      entete: 'Periode',
      rendu: (a) => (
        <span className="text-encre-500">
          {formaterDate(a.date_debut)} → {formaterDate(a.date_fin)}
        </span>
      ),
    },
    {
      cle: 'adhesion',
      entete: 'Adhesion',
      rendu: (a) => <span className="tabulaire">{formaterMontant(a.montant_adhesion)}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (a) => (
        <Badge
          ton={a.statut === 'active' ? 'succes' : a.statut === 'cloturee' ? 'neutre' : 'alerte'}
        >
          {a.statut}
        </Badge>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (a) => (
        <div className="flex justify-end gap-3">
          {a.statut === 'preparation' && peut('annee.creer') && (
            <button
              type="button"
              className="text-sm text-encre-500 underline hover:text-encre-900"
              onClick={() => void changerStatut(a, 'active')}
            >
              Activer
            </button>
          )}
          {a.statut === 'active' && peut('annee.cloturer') && (
            <button
              type="button"
              className="text-sm text-red-600 underline"
              onClick={() => void changerStatut(a, 'cloturee')}
            >
              Cloturer
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Annees scolaires"
        description="L’annee scolaire est commune a toutes les ecoles : la creer, l’activer ou la cloturer vaut partout. Elle court du 1er juillet au 30 juin, une seule est active en saisie, et les precedentes restent consultables."
        actions={
          <div className="flex items-center gap-3">
            <Link to="/annees/mode-operatoire" className="bouton-secondaire">
              Mode opératoire
            </Link>
            {peut('annee.creer') && (
              <button
                type="button"
                className="bouton-principal"
                onClick={() => setModaleOuverte(true)}
              >
                Nouvelle annee
              </button>
            )}
          </div>
        }
      />

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={donnees ?? []}
            cleDe={(a) => a.id}
            messageVide="Aucune annee scolaire."
          />
        </div>
      )}

      <FormulaireAnnee
        ouverte={modaleOuverte}
        anneeSource={donnees?.[0] ?? null}
        onFermer={() => setModaleOuverte(false)}
        onEnregistre={() => {
          setModaleOuverte(false);
          recharger();
        }}
      />
    </>
  );
}

function FormulaireAnnee({
  ouverte,
  anneeSource,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  /** L'annee la plus recente — la source de la reprise du referentiel. */
  anneeSource: AnneeScolaire | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const { entiteActive } = useAuth();
  const anneeCourante = new Date().getFullYear();
  const [valeurs, setValeurs] = useState({
    libelle: `${anneeCourante}-${String(anneeCourante + 1).slice(2)}`,
    dateDebut: `${anneeCourante}-07-01`,
    dateFin: `${anneeCourante + 1}-06-30`,
    adhesion: enEuros(2000),
  });
  // Le referentiel est ANNUEL : chaque case se (de)selectionne. Les
  // activites exigent leurs types.
  const [reprendreTypes, setReprendreTypes] = useState(true);
  const [reprendreActivites, setReprendreActivites] = useState(true);
  // Les types de reduction (2026-10-12) ne dependent pas des types d'activite.
  const [reprendreReductions, setReprendreReductions] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    // Les montants circulent en centimes entiers : la conversion se fait
    // ici, une fois, et jamais en flottant.
    const adhesion = enCentimes(valeurs.adhesion);

    if (adhesion === null) {
      setErreur('Montants invalides : utilisez le format 20,00.');
      return;
    }

    setEnCours(true);
    try {
      await api.post('/api/annees', {
        libelle: valeurs.libelle,
        dateDebut: valeurs.dateDebut,
        dateFin: valeurs.dateFin,
        montantAdhesion: adhesion,
        reprendreTypes: anneeSource ? reprendreTypes : false,
        reprendreActivites: anneeSource ? reprendreTypes && reprendreActivites : false,
        reprendreReductions: anneeSource ? reprendreReductions : false,
        anneeSourceId: anneeSource?.id,
      });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Creation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Nouvelle annee scolaire" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        {/* L'annee est commune : la creer la cree partout. La reprise du
            referentiel, elle, ne vaut que pour l'ecole affichee — chacune
            a ses tarifs. Le dire ici evite de chercher pourquoi l'autre
            ecole voit l'annee mais aucune activite. */}
        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          L’année scolaire est <strong>commune à toutes les écoles</strong> : elle apparaîtra
          partout. La reprise des types et activités ci-dessous ne concerne que
          {entiteActive?.code ? ` ${entiteActive.code}` : ' l’école affichée'} — chaque école
          reprend son propre référentiel depuis cet écran.
        </p>

        <Champ libelle="Libelle" aide="Format usuel : 2025-26.">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
            autoFocus
          />
        </Champ>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Debut">
            <input
              className="champ"
              type="date"
              value={valeurs.dateDebut}
              onChange={(e) => setValeurs({ ...valeurs, dateDebut: e.target.value })}
              required
            />
          </Champ>
          <Champ libelle="Fin">
            <input
              className="champ"
              type="date"
              value={valeurs.dateFin}
              onChange={(e) => setValeurs({ ...valeurs, dateFin: e.target.value })}
              required
            />
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Montant d’adhesion (€)">
            <input
              className="champ tabulaire"
              value={valeurs.adhesion}
              onChange={(e) => setValeurs({ ...valeurs, adhesion: e.target.value })}
              inputMode="decimal"
            />
          </Champ>
        </div>

        {anneeSource && (
          <fieldset className="space-y-2 rounded-md border border-encre-200 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">
              Reprendre de {anneeSource.libelle}
            </legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={reprendreTypes}
                onChange={(e) => {
                  setReprendreTypes(e.target.checked);
                  // Les activites ne vont pas sans leurs types.
                  if (!e.target.checked) setReprendreActivites(false);
                }}
              />
              <span>
                Types d’activité et tarifs
                <span className="mt-0.5 block text-xs text-encre-400">
                  Copiés tels quels — c’est sur la nouvelle année que vous réviserez les tarifs,
                  sans toucher à {anneeSource.libelle}.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={reprendreActivites}
                disabled={!reprendreTypes}
                onChange={(e) => setReprendreActivites(e.target.checked)}
              />
              <span>
                Activités
                <span className="mt-0.5 block text-xs text-encre-400">
                  Rattachées aux types repris. Les créneaux, eux, se reprennent depuis l’écran
                  Créneaux.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={reprendreReductions}
                onChange={(e) => setReprendreReductions(e.target.checked)}
              />
              <span>
                Types de réduction
                <span className="mt-0.5 block text-xs text-encre-400">
                  Copiés avec leur montant — à réviser sur la nouvelle année si besoin.
                </span>
              </span>
            </label>
          </fieldset>
        )}

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}
